/**
 * Number-format presets for self-service chart labels/axes. The preset KEYS are
 * defined in the builder JSON (`options.numberFormats[].value`) — this file only
 * holds the formatting LOGIC for each key, so adding a preset to the JSON needs a
 * matching case here (and nothing else). Single source of truth: both the DVM
 * layout builder and the recompute utils format through `formatNumber`.
 */

/** Format a numeric value using a builder `numberFormat` preset value. Unknown
 *  or 'NONE' presets return the raw number as-is.
 *
 *  `decimals` is the Format popup's explicit decimal-place count (0/1/2/…).
 *  When omitted, each preset falls back to its historical behavior (trimmed
 *  to at most 1 decimal, dropping a trailing `.0`); when given, that exact
 *  decimal count is always shown (no trimming) — "0 decimals" really means
 *  always show a whole number. */
export function formatNumber(value: number, format: string, decimals?: number): string {
  if (value == null || Number.isNaN(value)) return `${value ?? ''}`;
  const fractionDigits = decimals !== undefined && decimals !== null && Number.isFinite(decimals)
    ? Math.max(0, Math.min(6, Math.floor(decimals)))
    : undefined;
  switch (format) {
    case 'THOUSANDS':
      // Grouping separators: 1,234,567
      return value.toLocaleString('en-US', fractionDigits !== undefined
        ? { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits }
        : undefined);
    case 'COMPACT':
      // Auto-scale to the nearest K/M/B: 1.2K, 3.4M
      return new Intl.NumberFormat('en-US', {
        notation: 'compact',
        minimumFractionDigits: fractionDigits ?? 0,
        maximumFractionDigits: fractionDigits ?? 1,
      }).format(value);
    case 'SCALE_THOUSANDS':
      return formatScaled(value, 1e3, 'K', fractionDigits);
    case 'MILLIONS':
      return formatScaled(value, 1e6, 'M', fractionDigits);
    case 'BILLIONS':
      return formatScaled(value, 1e9, 'B', fractionDigits);
    case 'PERCENT':
      return `${fractionDigits !== undefined ? value.toFixed(fractionDigits) : trimFraction(value)}%`;
    case 'NONE':
    default:
      return fractionDigits !== undefined ? value.toFixed(fractionDigits) : `${value}`;
  }
}

/** Round to at most 1 decimal and drop a trailing `.0` (1.0 → "1", 1.25 → "1.3"). */
function trimFraction(value: number, maxFractionDigits = 1): string {
  const multiplier = 10 ** maxFractionDigits;
  const rounded = Math.round(value * multiplier) / multiplier;
  if (Number.isInteger(rounded)) return `${rounded}`;
  return rounded.toFixed(maxFractionDigits).replace(/\.?0+$/, '');
}

function formatScaled(value: number, divisor: number, suffix: string, fixedDecimals?: number): string {
  const scaled = value / divisor;
  if (fixedDecimals !== undefined) {
    return `${scaled.toFixed(fixedDecimals)}${suffix}`;
  }
  const absScaled = Math.abs(scaled);
  const digits = absScaled > 0 && absScaled < 1
    ? Math.min(9, Math.max(1, Math.ceil(-Math.log10(absScaled)) + 2))
    : 1;

  return `${trimFraction(scaled, digits)}${suffix}`;
}
